<template>
  <div>
    <!-------------------------------------------BUTTON-------------------------------------------->
    <v-row row wrap>
      <v-col xs="12">
        <v-row justify-center align-center>
          <v-btn variant="outlined" color="success" @click="openQrScanner">
            {{ $t("parts.insert_parts.repository.scan_repository") }}
          </v-btn>
          <v-btn icon variant="text" @click="showRepo = !showRepo">
            <v-icon v-if="showRepo"> mdi-menu-up </v-icon>
            <v-icon v-else> mdi-menu-down </v-icon>
          </v-btn>
        </v-row>
      </v-col>
    </v-row>
    <!--------------------------------------SELECTION FIELDS--------------------------------------->
    <v-row v-if="showRepo" wrap>
      <v-col xs="6" cols="12">
        <v-select
          v-model="repository"
          :items="repositories"
          :label="$t('parts.insert_parts.repository.select_repository')"
          color="red-darken-1"
          item-title="name"
          item-value="_id"
          return-object
        >
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :title="item.raw.name" />
          </template>
          <template #selection="{ item }">
            {{ item.raw.name }}
          </template>
        </v-select>
      </v-col>
      <v-col xs="6" cols="12">
        <v-select
          v-model="room"
          :items="repository.rooms || []"
          :label="$t('parts.insert_parts.repository.select_room')"
          color="red-darken-1"
          item-title="name"
          return-object
        >
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :title="item.raw.name" />
          </template>
          <template #selection="{ item }">
            {{ item.raw.name }}
          </template>
        </v-select>
      </v-col>
      <v-col xs="4" cols="12">
        <v-select
          v-model="location"
          :items="room.locations || []"
          :label="$t('parts.insert_parts.repository.select_location')"
          color="red-darken-1"
          item-title="code"
          return-object
        >
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :title="item.raw.code" />
          </template>
          <template #selection="{ item }">
            {{ item.raw.code }}
          </template>
        </v-select>
      </v-col>
      <v-col xs="4" cols="6">
        <v-select
          v-model="shelf.x"
          :items="xPositions"
          :label="$t('parts.insert_parts.repository.select_x')"
          color="red-darken-1"
        />
      </v-col>
      <v-col xs="4" cols="6">
        <v-select
          v-model="shelf.y"
          :items="yPositions"
          :label="$t('parts.insert_parts.repository.select_y')"
          color="red-darken-1"
        />
      </v-col>
    </v-row>
    <!---------------------------------------SCANNER DIALOG---------------------------------------->
    <v-dialog
      v-model="dialog"
      persistent
      max-width="720px"
      :fullscreen="$vuetify.display.smAndDown"
    >
      <v-card elevation="0">
        <v-container>
          <v-row>
            <!------------------------------------CAMERA SCANNER------------------------------------->
            <v-col v-if="!physicalScanner" cols="12">
              <div class="scanner-wrapper">
                <qrcode-stream
                  v-if="dialog"
                  :key="cameraFacing"
                  :camera="cameraFacing"
                  :paused="paused"
                  class="insert-parts-qr-stream"
                  @decode="decode"
                  @init="onInit"
                >
                  <template #error="err">
                    <v-alert type="error" variant="tonal">
                      {{ err?.message || $t("Cannot access camera.") }}
                    </v-alert>
                  </template>
                </qrcode-stream>
                <div
                  v-if="cameraLoading"
                  class="scanner-loading-overlay"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                  />
                </div>
              </div>
            </v-col>
            <!------------------------------------PHYSICAL SCANNER----------------------------------->
            <v-col v-else cols="12">
              <v-row class="my-2" justify="center">
                <div
                  v-if="found === null && !physicalScannerError"
                  class="ma-3 text-blue text-subtitle-1"
                >
                  {{ $t("parts.insert_parts.repository.physical_scanner") }}
                </div>
                <div
                  v-else-if="physicalScannerError"
                  class="ma-3 text-red text-subtitle-1"
                >
                  {{ $t("parts.insert_parts.repository.physical_scanner_error") }}
                </div>
              </v-row>
            </v-col>
            <!--------------------------------------------------------------------------------------->
            <v-col cols="12" class="text-center">
              <v-progress-circular
                v-if="loading"
                indeterminate
                color="primary"
              />
              <span v-if="found === true" class="text-green text-subtitle-1">
                {{
                  $t("parts.insert_parts.repository.found_repository_message")
                }}
                {{ decodedString }}
              </span>
              <span v-else-if="found === false" class="text-red text-subtitle-1">
                {{
                  $t(
                    "parts.insert_parts.repository.not_found_repository_message",
                  )
                }}
                {{ decodedString }}
              </span>
              <span v-else-if="found === null" class="text-subtitle-1">
                {{ $t("parts.insert_parts.repository.scan_shelf_message") }}
              </span>
            </v-col>

            <v-col v-if="found === true" cols="12" class="text-center">
              <v-btn variant="outlined" color="success" @click="addLocation">
                {{ $t("parts.insert_parts.repository.add_location") }}
              </v-btn>
            </v-col>

            <!-- BOTTOM ACTION BUTTONS -->
            <v-col cols="12">
              <v-row class="pa-2 align-center">
                <v-btn icon variant="text" @click="reScan">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>

                <v-spacer />

                <v-btn icon variant="text" @click="switchScanner">
                  <v-icon>mdi-qrcode</v-icon>
                </v-btn>

                <v-btn
                  v-if="!physicalScanner"
                  icon
                  variant="text"
                  @click="switchCamera"
                >
                  <v-icon>mdi-camera-switch</v-icon>
                </v-btn>

                <v-btn variant="outlined" color="error" @click="close">
                  {{ $t("parts.insert_parts.repository.close") }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <!--------------------------------------------------------------------------------------------->
  </div>
</template>

<script>
import getRequest from "../../api/getRequest";
import { QrcodeStream } from "vue3-qrcode-reader";
export default {
  name: "Repository",
  components: {
    QrcodeStream,
  },
  props: {
    part: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    dialog: false,
    showRepo: false,
    /** Prefer rear on mobile; 'auto' falls back to front/webcam on desktop. */
    cameraFacing: "auto",
    repositories: [],
    repository: {},
    room: {},
    location: {},
    shelf: {},
    xPositions: [],
    yPositions: [],
    paused: false,
    cameraLoading: false,
    loading: false,
    found: null,
    decodedString: null,
    shelfData: {},
    physicalScanner: false,
    physicalScannerError: false,
    buffer: "",
    autocompletion: false,
  }),
  watch: {
    repository(newVal) {
      if (!this.autocompletion && newVal && Object.keys(newVal).length) {
        this.room = {};
        this.location = {};
        this.shelf = {};
      }
    },
    room(newVal) {
      if (!this.autocompletion && newVal && Object.keys(newVal).length) {
        this.location = {};
        this.shelf = {};
      }
    },
    location(value) {
      if (value.position_x && value.position_y) {
        this.generateCoordinates(value.position_x, value.position_y);
      }
      if (this.autocompletion === false) {
        this.shelf = {};
      }
    },
    shelf: {
      handler(value) {
        if (value.x && value.y) {
          const shelf = this.location.shelves.find(
            (s) => s.x === value.x && s.y === value.y,
          );
          this.$emit("location", {
            repository_id: this.repository._id,
            room_id: this.room._id,
            location_id: this.location._id,
            shelf_id: shelf._id,
          });
        }
      },
      deep: true,
    },
    part: {
      handler(newPart) {
        if (newPart && Object.keys(newPart).length) {
          this.autocompletion = true;
          this.open(newPart);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    // this.getRepositories();
  },
  methods: {
    async onInit(promise) {
      this.cameraLoading = true;
      try {
        await promise;
      } catch (err) {
        if (err.name === "NotAllowedError") {
          this.switchScanner();
        } else if (
          (err.name === "OverconstrainedError" ||
            err.name === "NotFoundError") &&
          this.cameraFacing !== "auto"
        ) {
          this.cameraFacing = "auto";
        }
      } finally {
        this.cameraLoading = false;
      }
    },
    /* SCANNER ---------------------------------------------------------------------------------- */
    openQrScanner() {
      this.cameraFacing = "auto";
      this.cameraLoading = true;
      this.$emit("hide", true);
      this.dialog = true;
    },
    decode(value) {
      if (value !== null && value.length > 0) {
        this.found = null;
        this.paused = true;
        this.decodedString = value;
        this.getShelf(value);
      }
    },
    addLocation() {
      this.repository = this.shelfData.repository;
      this.room = this.repository.rooms[this.shelfData.roomIndex];
      this.location = this.room.locations[this.shelfData.locationIndex];
      this.shelf = this.shelfData.shelf;

      this.close();
    },
    reScan() {
      this.decodedString = null;
      this.found = null;
      this.loading = false;
      this.paused = false;
      this.physicalScannerError = false;
    },
    switchCamera() {
      this.physicalScanner = false;
      document.removeEventListener("keydown", this.listenToEvent);
      this.cameraLoading = true;
      this.cameraFacing = this.cameraFacing === "front" ? "rear" : "front";
    },
    switchScanner() {
      this.physicalScanner = true;
      this.cameraLoading = false;
      document.addEventListener("keydown", this.listenToEvent);
    },
    listenToEvent(e) {
      if (e.key !== "Enter") {
        this.buffer += e.key;
      } else if (this.buffer.length > 24) {
        this.physicalScannerError = true;
        this.buffer = "";
      } else {
        this.decode(this.buffer);
        this.buffer = "";
      }
    },
    open(data) {
      // this.part = data;
      this.getRepositories();
    },
    close() {
      this.$emit("hide", false);
      this.dialog = false;
      this.paused = false;
      this.loading = false;
      this.found = null;
      this.decodedString = null;
      this.shelfData = {};
      this.physicalScanner = false;
      this.physicalScannerError = false;
      this.cameraFacing = "auto";
      this.cameraLoading = false;
      document.removeEventListener("keydown", this.listenToEvent);
    },
    /* REPOSITORY ------------------------------------------------------------------------------- */
    getShelf(id) {
      this.loading = true;
      getRequest("/repository/shelf", `id=${id}`)
        .then((response) => {
          this.found = true;
          this.loading = false;
          this.shelfData = response;
        })
        .catch(() => {
          this.found = false;
          this.loading = false;
        });
    },
    getRepositories() {
      getRequest(
        "/repository/",
        `role=${this.$store.getters.role}&institution_id=${
          this.$store.getters.institutionId
        }`,
      )
        .then((response) => {
          this.repositories = response.repositories;
          this.autocompleteLocation(this.repositories);
        })
        .catch(() => {});
    },
    autocompleteLocation(repositories) {
      this.autocompletion = true;

      this.$nextTick(async () => {
        if (this.part.repository_id) {
          this.repository =
            repositories.find((r) => r._id === this.part.repository_id) || {};
        }
        await this.$nextTick();
        if (this.repository.rooms && this.part.room_id) {
          this.room =
            this.repository.rooms.find((r) => r._id === this.part.room_id) ||
            {};
        }
        await this.$nextTick();
        if (this.room.locations && this.part.location_id) {
          this.location =
            this.room.locations.find((l) => l._id === this.part.location_id) ||
            {};
        }
        await this.$nextTick();
        if (this.location.shelves && this.part.shelf_id) {
          const shelf = this.location.shelves.find(
            (s) => s._id === this.part.shelf_id,
          );
          if (shelf) {
            this.shelf = { x: shelf.x, y: shelf.y };
            this.generateCoordinates(
              this.location.position_x,
              this.location.position_y,
            );
          }
        }
        setTimeout(() => {
          this.autocompletion = false;
        }, 250);
      });
    },
    generateCoordinates(x, y) {
      const xArr = [];
      const yArr = [];
      for (let xc = 1; xc <= x; xc += 1) {
        xArr.push(xc);
      }
      for (let yc = 1; yc <= y; yc += 1) {
        yArr.push(yc);
      }
      this.xPositions = xArr;
      this.yPositions = yArr;
    },
    /* ------------------------------------------------------------------------------------------ */
    clear() {
      this.shelfData = {};
      this.repositories = [];
      this.repository = {};
      this.room = {};
      this.location = {};
      this.shelf = {};
      this.xPositions = [];
      this.yPositions = [];
      this.dialog = false;
      this.showRepo = false;
      this.paused = false;
      this.loading = false;
      this.found = null;
      this.decodedString = null;
      this.physicalScanner = false;
      this.physicalScannerError = false;
      this.cameraFacing = "auto";
      this.cameraLoading = false;
      document.removeEventListener("keydown", this.listenToEvent);
    },
  },
};
</script>

<style scoped>
.scanner-wrapper {
  position: relative;
  width: 100%;
  min-height: 200px;
}

.scanner-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
}

.insert-parts-qr-stream {
  display: block;
  width: 100%;
  max-height: 55vh;
}

.insert-parts-qr-stream :deep(video) {
  width: 100%;
  max-height: 55vh;
  object-fit: cover;
}

@media (max-width: 959.98px) {
  .insert-parts-qr-stream,
  .insert-parts-qr-stream :deep(video) {
    max-height: calc(100dvh - 280px);
  }
}
</style>
